import { FC, useEffect, useMemo } from "react";
import { Grid } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import { ConsumptionIntervalModelType, SitesModelType, SitesSourceModelType } from "../../../../../common/modelTypes";
import { colGrow } from "../../../../../common/utils";
import ComparisonLinearProgress from "../../../../../components/charts/graph/ComparisonLinearProgress";
import NumericValueWidget from "../../../../../components/charts/NumericValueWidget";
import { AmbientalKpiWidget } from "../../../../../components/common/AmbientalKpiWidget";
import ContentLocked from "../../../../../components/common/ContentLocked";
import { MarketBaseType, MarketBaseWidget } from "../../../../../components/common/MarketBaseWidget";
import PermissionGridItem from "../../../../../components/common/PermissionGridItem";
import TableData from "../../../../../components/common/table/TableData";
import { OperationalUnitsGreenrouterTabConsumptions } from "../../../../../context/PageConfigrations/pageConfiguration.types";
import {
	ConsumptionIntervalResultsDTO,
	CountriesEmissionsResultsDTO,
	ExtendedLabeledValueDTO,
	NodesMapDTO,
} from "../../../../../datasources/api-client/data-contracts";
import { getLockedContents } from "../../../../../permissions/permissionUtils";
import { EmissionsTypesColors } from "../../../../../theme/themeValues";
import EmissionsIntervals from "../../../components/ResultsEmissionsIntervals";
import { MapBounds } from "../../useOperationalUnitsData";
import MapEmissionNodes from "./components/MapEmissionNodes";
import { dataColSitesSource, getColumnsSitesSource, getRowsSitesSource } from "./data";

export type EmissionConsumptionKey = keyof ConsumptionIntervalResultsDTO;

export type SourcesTable = {
	rows: { id: string | number; [key: string]: any }[];
	columns: GridColDef[];
};

export type MapsEmissionsNodesType = {
	nodes: {
		nodesData: NodesMapDTO | undefined;
		isLoading: boolean | undefined;
		isForbidden: boolean;
		reload: VoidFunction | undefined;
	};
	emissions: {
		emissionData: CountriesEmissionsResultsDTO | undefined;
		isLoading: boolean | undefined;
		isForbidden: boolean;
		reload: VoidFunction | undefined;
	};
};

interface Props {
	emissionsSites: SitesModelType;
	from: string;
	to: string;
	emissionsSitesSource: SitesSourceModelType;
	emissionsConsumptionIntervals: ConsumptionIntervalModelType;
	consumptionSources: { electricity: SourcesTable } | undefined;
	loadingSources: boolean;
	consumptionReload: VoidFunction;
	emissionsIntervalsPeriodType: "month" | "quarter" | "four-month" | "semester";
	setEmissionsIntervalsPeriodType: (periodType: "month" | "quarter" | "four-month" | "semester") => void;
	maps: MapsEmissionsNodesType;
	onBoundsChange: (bounds: MapBounds) => void;
	mapBounds: MapBounds | undefined;
	greenrouterSelectedTabConsumption: OperationalUnitsGreenrouterTabConsumptions;
	setGreenrouterSelectedTabConsumption: (tab: OperationalUnitsGreenrouterTabConsumptions) => void;
}

const Greenrouter: FC<Props> = ({
	emissionsSites,
	emissionsSitesSource,
	emissionsConsumptionIntervals,
	consumptionSources,
	loadingSources,
	consumptionReload,
	emissionsIntervalsPeriodType,
	setEmissionsIntervalsPeriodType,
	maps,
	onBoundsChange,
	mapBounds,
	greenrouterSelectedTabConsumption,
	setGreenrouterSelectedTabConsumption,
}) => {
	const { t } = useTranslation("feature.units");

	// Comparison data for operational units with/without consumption
	const fromEmissionsDatasource = emissionsSites && {
		name: t("overview/with consumption"),
		value: emissionsSites?.data?.greenRouter.current.countWithWtw.percentage,
	};
	const toEmissionsDatasource = emissionsSites && {
		name: t("overview/without consumption"),
		value: emissionsSites?.data?.greenRouter.current.countWithoutWtw.percentage,
	};

	// Memoize dropdown items
	const dropdownItems = useMemo(() => {
		const data = emissionsConsumptionIntervals.data;
		if (!data) return [];

		return (Object.keys(data) as OperationalUnitsGreenrouterTabConsumptions[]).map((key) => ({
			key,
			label: `${t(`greenrouter/consumption emission/${key}`)} ${data[key].measure}`,
		}));
	}, [emissionsConsumptionIntervals.data, t]);

	// Update greenrouterSelectedTabConsumption when dropdownItems change
	useEffect(() => {
		if (dropdownItems.length > 0 && !greenrouterSelectedTabConsumption) {
			setGreenrouterSelectedTabConsumption(dropdownItems[0].key);
		}
	}, [dropdownItems, greenrouterSelectedTabConsumption]);

	// Handle the change of graphics tab and store it to local storage
	const handleSelectKeyChange = (value: OperationalUnitsGreenrouterTabConsumptions | string) => {
		setGreenrouterSelectedTabConsumption(value as OperationalUnitsGreenrouterTabConsumptions);
	};

	// Compute chart data and measure for the selected interval.
	const { chartData, chartMeasure } = useMemo(() => {
		if (!emissionsConsumptionIntervals.data || !greenrouterSelectedTabConsumption) {
			return { chartData: [], chartMeasure: "" };
		}

		// Data for the selected category
		const interval = emissionsConsumptionIntervals.data[greenrouterSelectedTabConsumption];

		if (!interval?.data?.length) {
			return { chartData: [], chartMeasure: interval?.measure ?? "" };
		}

		const base = interval.data;

		const chartData = base.map((item: ExtendedLabeledValueDTO) => {
			const result: any = {
				label: item.label,
				value: item.value,
			};

			if (Array.isArray(item.details)) {
				item.details.forEach((detail: ExtendedLabeledValueDTO) => {
					result[detail.label] = detail.value;
				});
			}

			return result;
		});

		return {
			chartData,
			chartMeasure: interval.measure ?? "",
		};
	}, [emissionsConsumptionIntervals, greenrouterSelectedTabConsumption]);

	// Assign chart colors based on emission type
	const chartColors: Record<EmissionConsumptionKey, string> = {
		electricity: EmissionsTypesColors.LightElectricity,
		fuel: EmissionsTypesColors.LightFuel,
		heatSteam: EmissionsTypesColors.LightHeat,
		refrigerants: EmissionsTypesColors.LightRefrigerant,
		processGas: EmissionsTypesColors.LightProcessGases,
		water: EmissionsTypesColors.LightWater,
	};
	const chartColor = greenrouterSelectedTabConsumption && chartColors[greenrouterSelectedTabConsumption];

	// Determine source emission table for the selected key
	const sourceEmission =
		greenrouterSelectedTabConsumption && consumptionSources
			? consumptionSources[greenrouterSelectedTabConsumption as keyof typeof consumptionSources]
			: undefined;

	const marketBase: MarketBaseType = {
		emissions: emissionsSites.data?.greenRouter?.current.marketBasedWtw,
		delta: emissionsSites.data?.greenRouter?.current.marketBasedWtwDelta,
		coverage: null,
		isLoading: emissionsSites.isLoading,
		onReload: emissionsSites.reload,
	};

	const titles = {
		totalEmissions: t("overview/total emissions"),
		operationalUnits: t("overview/no operational units"),
		map: t("greenrouter/distribution of emissions and operating units"),
		emissionsBySource: t("greenrouter/emissions and consumption by source"),
		// card title of the intervals chart (set by EmissionsIntervals)
		consumptionIntervals: t("overview/consumption trends and details"),
		environmentalKpis: t("common:environmental kpis"),
		marketBased: t("common:market-based measures"),
	};

	const lockedContents = getLockedContents([
		{
			models: [emissionsSites],
			names: [titles.totalEmissions, titles.operationalUnits, titles.environmentalKpis, titles.marketBased],
		},
		{ models: [maps.nodes, maps.emissions], names: [titles.map] },
		{ models: [emissionsSitesSource], names: [titles.emissionsBySource] },
		{ models: [emissionsConsumptionIntervals], names: [titles.consumptionIntervals] },
	]);

	return (
		<Grid container spacing={2}>
			<Grid container size={12} spacing={2}>
				<PermissionGridItem models={[emissionsSites]} size={colGrow} minHeight={150}>
					<NumericValueWidget
						title={titles.totalEmissions}
						value={emissionsSites?.data?.greenRouter.current.co2Wtw.value}
						delta={emissionsSites?.data?.greenRouter.delta.co2Wtw}
						measure={emissionsSites?.data?.greenRouter.current.co2Wtw.measure}
						isLoading={emissionsSites.isLoading}
						onReload={emissionsSites.reload}
					/>
				</PermissionGridItem>
				<PermissionGridItem models={[emissionsSites]} size={colGrow} minHeight={150}>
					<NumericValueWidget
						title={titles.operationalUnits}
						value={emissionsSites?.data?.greenRouter.current.count}
						isLoading={emissionsSites.isLoading}
						onReload={emissionsSites.reload}
						graph={<ComparisonLinearProgress from={fromEmissionsDatasource} to={toEmissionsDatasource} />}
					/>
				</PermissionGridItem>
			</Grid>

			<Grid container size={12} spacing={2}>
				<PermissionGridItem models={[maps.nodes, maps.emissions]} size={colGrow} minHeight={400}>
					<MapEmissionNodes maps={maps} onBoundsChange={onBoundsChange} mapBounds={mapBounds} />
				</PermissionGridItem>
				<PermissionGridItem models={[emissionsSitesSource]} size={colGrow} minHeight={400}>
					<TableData
						columns={getColumnsSitesSource(dataColSitesSource, t)}
						rows={emissionsSitesSource.data ? getRowsSitesSource(emissionsSitesSource.data, t) : []}
						title={titles.emissionsBySource}
						isLoading={emissionsSitesSource.isLoading}
						onReload={emissionsSitesSource.reload}
					/>
				</PermissionGridItem>
			</Grid>

			<PermissionGridItem models={[emissionsConsumptionIntervals]} size={12} minHeight={400}>
				<EmissionsIntervals
					mode="tabs"
					selectTitle={t("greenrouter/consumption trends")}
					onReload={emissionsConsumptionIntervals.reload}
					isLoading={emissionsConsumptionIntervals.isLoading && loadingSources}
					menuOptions={dropdownItems}
					selectedKey={greenrouterSelectedTabConsumption}
					handleSelectKeyChange={handleSelectKeyChange}
					chartData={chartData}
					chartMeasure={chartMeasure}
					chartColor={chartColor}
					titleSourceEmission={t("greenrouter/consumption trends and details")}
					sourceEmission={sourceEmission}
					consumptionReload={consumptionReload}
					periodType={emissionsIntervalsPeriodType}
					setPeriodType={setEmissionsIntervalsPeriodType}
				/>
			</PermissionGridItem>

			<PermissionGridItem models={[emissionsSites]} size={12}>
				<AmbientalKpiWidget
					bio={emissionsSites.data?.greenRouter.current.biogenic}
					loading={emissionsSites.isLoading}
					onReload={emissionsSites.reload}
				/>
			</PermissionGridItem>

			<PermissionGridItem models={[emissionsSites]} size={12}>
				<MarketBaseWidget marketBase={marketBase} />
			</PermissionGridItem>

			{lockedContents.length > 0 && (
				<Grid size={12}>
					<ContentLocked items={lockedContents} />
				</Grid>
			)}
		</Grid>
	);
};

export default Greenrouter;
